import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, ArrowRight } from "lucide-react";

// Swap this URL with your own hosted video (e.g. S3 / Cloudflare R2 / Bunny CDN link)
const INTRO_VIDEO_URL = process.env.REACT_APP_INTRO_VIDEO_URL || "/adulis-intro.mp4";

const VideoOverlay = ({ onEnter }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (videoRef.current) videoRef.current.muted = next;
  };

  return (
    <motion.div
      data-testid="video-overlay"
      className="fixed inset-0 z-[100] bg-canvas/50 backdrop-blur-2xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.8 } }}
      exit={{ opacity: 0, transition: { duration: 1, ease: [0.65, 0, 0.35, 1] } }}
    >
      <video
        ref={videoRef}
        data-testid="intro-video"
        src={INTRO_VIDEO_URL}
        className="absolute inset-0 w-full h-full object-contain"
        autoPlay
        loop
        muted
        playsInline
      />
      <div className="absolute inset-0 bg-gradient-to-t from-canvas/70 via-transparent to-canvas/30 pointer-events-none" />

      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 sm:px-12 py-8">
        <motion.span
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.4, duration: 0.8 } }}
          className="font-display font-bold text-xl tracking-tight"
        >
          adulis<span className="text-clay">.io</span>
        </motion.span>
        <motion.button
          data-testid="mute-toggle-btn"
          onClick={toggleMute}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.8 } }}
          className="flex items-center gap-2 border border-ink/30 rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold backdrop-blur-md bg-canvas/30 hover:bg-canvas/60 transition-colors duration-300"
          aria-label={muted ? "Unmute video" : "Mute video"}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          {muted ? "Sound off" : "Sound on"}
        </motion.button>
      </div>

      <div className="absolute bottom-0 left-0 right-0 px-6 sm:px-12 pb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.7, duration: 0.9 } }}
          className="max-w-xs text-sm text-ink-dim uppercase tracking-[0.2em] leading-relaxed"
        >
          Digital public infrastructure, born in Ethiopia — built for Africa
        </motion.p>
        <motion.button
          data-testid="enter-site-btn"
          onClick={onEnter}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.9, duration: 0.9 } }}
          className="btn-pill btn-pill-solid group"
        >
          Enter Adulis
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default VideoOverlay;
